import { useGetParcellingById } from '@practium/features/api';
import { useEffect, useMemo } from 'react';
import type { FertilizationPlanAdvice, MaintenanceFormData, MaintenanceParcellingType } from '../types';
import { getMaintenancePreviewStart, getMaintenanceReferenceId } from '../utils/maintenanceAdviceMapper';
import {
	CYCLE_START_PHASES,
	FLOWERING_PHASES,
	getParcellingMonthsByPhase,
	toMonthSelectOptions,
	type MaintenanceParcellingContext,
} from '../utils/parcellingCycle';
import { mapParcellingToTableRows } from '../utils/parcellingTableMapper';

interface UseMaintenanceParcellingOptions {
	form: MaintenanceFormData;
	set: <K extends keyof MaintenanceFormData>(key: K, value: MaintenanceFormData[K]) => void;
	viewData?: FertilizationPlanAdvice;
	isView: boolean;
	isEdit: boolean;
	onContextChange: (context: MaintenanceParcellingContext) => void;
}

export const useMaintenanceParcelling = ({
	form,
	set,
	viewData,
	isView,
	isEdit,
	onContextChange,
}: UseMaintenanceParcellingOptions) => {
	const selectedParcellingId = isView
		? getMaintenanceReferenceId(viewData?.parcelling)
		: form.parcelling;
	const { data: selectedParcelling } = useGetParcellingById(selectedParcellingId);

	const cycleStartMonths = useMemo(
		() => getParcellingMonthsByPhase(selectedParcelling?.fixedParcel, CYCLE_START_PHASES),
		[selectedParcelling]
	);
	const floweringMonths = useMemo(
		() => getParcellingMonthsByPhase(selectedParcelling?.fixedParcel, FLOWERING_PHASES),
		[selectedParcelling]
	);
	const cycleStartOptions = useMemo(
		() => toMonthSelectOptions(cycleStartMonths),
		[cycleStartMonths]
	);

	useEffect(() => {
		onContextChange({ floweringMonths, cycleStartMonths });
	}, [cycleStartMonths, floweringMonths, onContextChange]);

	useEffect(() => {
		if (form.parcellingType || !selectedParcelling?.typeParcelling) return;
		set('parcellingType', selectedParcelling.typeParcelling);
	}, [form.parcellingType, selectedParcelling?.typeParcelling, set]);

	useEffect(() => {
		if (form.parcellingType !== 'fixed' || !form.cycleStartMonth || !cycleStartMonths.length) return;
		if (!cycleStartMonths.includes(Number(form.cycleStartMonth))) {
			set('cycleStartMonth', '');
		}
	}, [cycleStartMonths, form.cycleStartMonth, form.parcellingType, set]);

	const previewStart = isView
		? getMaintenancePreviewStart(viewData)
		: form.parcellingType === 'fixed'
			? form.cycleStartMonth
			: undefined;
	const previewRows = useMemo(
		() => mapParcellingToTableRows(selectedParcelling, previewStart),
		[previewStart, selectedParcelling]
	);

	const showParcellingFields = isEdit || !!form.parcellingType;
	const showCycleStartField = showParcellingFields && form.parcellingType === 'fixed' && !!form.parcelling;
	const showParcellingPreview = previewRows.length > 0 && (isView || !!form.parcelling);

	const resetParcellingFields = () => {
		set('parcelling', '');
		set('cycleStartMonth', '');
	};

	const setParcellingType = (value: MaintenanceParcellingType | '') => {
		set('parcellingType', value);
		resetParcellingFields();
		set('floweringMonth', '');
	};

	const setParcelling = (value: string) => {
		set('parcelling', value);
		set('cycleStartMonth', '');
	};

	return {
		cycleStartOptions,
		previewRows,
		showParcellingFields,
		showCycleStartField,
		showParcellingPreview,
		resetParcellingFields,
		setParcellingType,
		setParcelling,
	};
};
